import React, { useState, useEffect } from 'react';
import styles from './GamesTab.module.scss';
import { Pencil, Trash2, Plus, Minus, X, Image as ImageIcon, ArrowRight} from 'lucide-react';
// import { motion, AnimatePresence } from 'framer-motion';
import { DEBUG, PATH } from '../../../../utils/constants';

import Collapse from '../../../../utils/Collapse/Collapse';
import RichTextEditor from '../../../../utils/RichTextEditor/RichTextEditor';
import MarkdownRenderer from '../../../../utils/MarkdownRenderer/MarkdownRenderer';
import SmartRenderer from '../../../../utils/SmartRenderer/SmartRenderer';
import Flag from '../../../../utils/Flags/Flags';
import ImageEditor from '../../../../utils/ImageEditor/ImageEditor';

import { updateGameImage  } from "../../../../utils/api/gamesAPI";

const debug = DEBUG;


function GamesTab({ 
  games, 
  gameInfos,
  onCreateGame, 
  onUpdateGame, 
  onDeleteGame, 
  onGameImageUpdated,
  onCreateGameInfos, 
  onUpdateGameInfos,
  onDeleteGameInfos,
  onToggleGameInfosActive,
  onRegenerateCode,
  onGameInfosSelect
}) {

  // Plusieurs panneaux peuvent rester ouverts
  const [expandedGameIds, setExpandedGameIds] = useState(() => new Set());
  // Mémoriser la langue sélectionnée par jeu : { [gameId]: gameInfosObj }
  const [selectedInfoByGame, setSelectedInfoByGame] = useState({});

  // ============= Ajouter un jeu ============== //
  const [gameName, setGameName] = useState("");
  const [regenMessages, setRegenMessages] = useState({});

  // ============= Editer un jeu ============== //
  const [isGameEditModalOpen, setIsGameEditModalOpen] = useState(false);
  const [editingGame, setEditingGame] = useState(null);
  const [editedGameName, setEditedGameName] = useState('');
  const [editedGameName_En, setEditedGameName_En] = useState('');
  const [editedGameName_De, setEditedGameName_De] = useState('');
  const [editedGameName_Es, setEditedGameName_Es] = useState('');
  const [editedGameName_It, setEditedGameName_It] = useState('');
  // --- Tabs dans la modale d'édition
  const [activeEditTab, setActiveEditTab] = useState('names'); // 'names' | 'partner'
  // --- Formulaire partenaire dans la modale
  const [partnerForm, setPartnerForm] = useState({ company_name: '', company_address: '', company_phone: '', company_email: ''});

  // ============= Editer l'image d'un jeu ============== //
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [imageTargetGameId, setImageTargetGameId] = useState(null);
  const [imagePreviewPath, setImagePreviewPath] = useState(null);

  // Mise à jour de l'image du jeu
  const [imageUpdateMessage, setImageUpdateMessage] = useState(null);
  // Pour mise à jour de l'image dans le composant ImageEditor :
  const [canUpdatePreview, setCanUpdatePreview] = useState(false);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("add"); // 'add' ou 'edit'
  const [editingInfo, setEditingInfo] = useState(null);
  const [targetGameId, setTargetGameId] = useState(null);
  const [formLang, setFormLang] = useState("");
  const [formIntro, setFormIntro] = useState("");
  const [formOutro, setFormOutro] = useState("");

  const ALL_LANGUAGES = [
    { code: "fr", label: "🇫🇷 Français" },
    { code: "en", label: "🇬🇧 English" },
    { code: "de", label: "🇩🇪 Deutsch" },
    { code: "es", label: "🇪🇸 Español" },
    { code: "it", label: "🇮🇹 Italiano" }
  ];

  // Si les infos d'une langue changent, on resynchro pour les jeux concernés
  useEffect(() => {
    setSelectedInfoByGame((prev) => {
      const next = { ...prev };
      for (const [gameId, info] of Object.entries(prev)) {
        const fresh = gameInfos.find((gi) => gi.id === info.id);
        if (fresh) next[gameId] = fresh;
      }
      return next;
    });
  }, [gameInfos]);
  

  const handleCreateGame = (e) => {
    e.preventDefault();
    if (!gameName.trim()) return;

    const newGame = onCreateGame(gameName);
    if (newGame) {
      setGameName('');
    }
  };

  // Gestion modale mise à jour de l'image
  const openImageModal = (gameId) => {
    const current = games.find(g => g.id === gameId);
    setImageTargetGameId(gameId);
    setImagePreviewPath(current?.image ? `${PATH}${current.image}` : null);
    setImageModalOpen(true);
  };

  // Gestion modales gameInfos (ajout et modifications)
  const openAddModal = (gameId) => {
    setTargetGameId(gameId);
    setModalMode("add");
    setFormLang("");
    setFormIntro("");
    setFormOutro("");
    setIsModalOpen(true);
  };

  const openEditModal = (info) => {
    setTargetGameId(info.id_game);
    setEditingInfo(info);
    setModalMode("edit");
    setFormLang(info.lang || "");
    setFormIntro(info.intro || "");
    setFormOutro(info.outro || "");
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingInfo(null);
  };

  const handleModalSubmit = () => {
    const payload = { lang: formLang, intro: formIntro, outro: formOutro };
    if (modalMode === "add") onCreateGameInfos?.(targetGameId, payload);
    else onUpdateGameInfos?.(editingInfo.id, payload);
    
    closeModal();
  };

  // Ouverture des tabs
  const handleLangClick = (gameId, info) => {
    const isOpen = expandedGameIds.has(gameId);
    const current = selectedInfoByGame[gameId];

    if (isOpen) {
      if (current?.lang === info.lang) {
        // même langue cliquée -> on referme
        setExpandedGameIds(prev => {
          const next = new Set(prev);
          next.delete(gameId);
          return next;
        });
      } else {
        // autre langue du même jeu -> on remplace juste le contenu, sans refermer
        setSelectedInfoByGame(prev => ({ ...prev, [gameId]: info }));
      }
    } else {
      // panneau fermé -> on ouvre + on sélectionne cette langue
      setSelectedInfoByGame(prev => ({ ...prev, [gameId]: info }));
      setExpandedGameIds(prev => {
        const next = new Set(prev);
        next.add(gameId);
        return next;
      });
    }
  };

  // Filtrage des langues non existantes
  const getAvailableLanguagesForGame = (gameId) => {
    const usedLangs = gameInfos
      .filter(info => info.id_game === gameId)
      .map(info => info.lang);

    return ALL_LANGUAGES.filter(lang => !usedLangs.includes(lang.code));
  };

  const isCheckedFor = (info) => (info ? !!info.active : false);


  // ============= Editer un jeu ============== //

  // Changement des valeurs du formulaire (infos partenaire)
  const onPartnerChange = (e) => {
    const { name, value } = e.target;
    setPartnerForm(f => ({ ...f, [name]: value }));
  };

  // Filtrage des langues existantes
  const getExistingLanguagesForGame = (gameId, { exclude = [] } = {}) => {
    const used = new Set(
      (gameInfos || [])
      .filter(info => info?.id_game === gameId && info?.lang)
      .map(info => String(info.lang).toLowerCase())
    );

    return ALL_LANGUAGES.filter(l => used.has(l.code)).filter(l => !exclude.includes(l.code)); // exclure 'fr'
  };

  // mapping code -> getter / setter des états locaux pour l'affichage des champs de modification des traduction dans la modale d'édition du jeu
  const getLangValue = (code) => {
  switch (code) {
    case 'en': return editedGameName_En;
    case 'de': return editedGameName_De;
    case 'es': return editedGameName_Es;
    case 'it': return editedGameName_It;
    default:   return '';
  }
  };
  const setLangValue = (code, v) => {
  switch (code) {
    case 'en': return setEditedGameName_En(v);
    case 'de': return setEditedGameName_De(v);
    case 'es': return setEditedGameName_Es(v);
    case 'it': return setEditedGameName_It(v);
    default:   return;
  }
  };
  // const normalize = (s) => (s?.trim() ? s.trim() : null);

  const existingNonFr = getExistingLanguagesForGame(editingGame?.id, { exclude: ['fr'] });



  return (
    <div className={styles.tabContainer}>
      <div className={styles.mainSection}>
        <h2 className={styles.title}>Interface de gestion des jeux insolites</h2>

        {/* ======= Création d'un nouveau jeu ======= */}
        <form onSubmit={handleCreateGame} className={styles.form}>
          <label htmlFor="gameName" className={styles.label}>Commençons par créer un nouveau jeu (vous pourrez en éditer les détails dans la liste ci-dessous)</label>
          <div className={styles.formGroup}>
            <input
              id="gameName"
              type="text"
              value={gameName}
              onChange={(e) => setGameName(e.target.value)}
              placeholder="Entrez le nom du jeu"
              required
              className={styles.input}
            />
              
          </div>
          <button type="submit" className={styles.submitButton}><ArrowRight size={16}/>Créer le jeu</button>
        </form>

        <hr className={styles.hr} />

        {/* ======= Construction de la liste de gestion des jeux ======= */}
        {games.map((game) => {
          const isOpen = expandedGameIds.has(game.id);
          const relatedInfos = gameInfos.filter((info) => info.id_game === game.id);
          const selectedGameInfos = selectedInfoByGame[game.id] || relatedInfos[0] || null;

          return (
            <div key={game.id} className={styles.gameBlock}>
              <div className={styles.gameHeader}>

                  <div className={styles.gameTitle}>
                    <div className={styles.gameTitleRow}>
                      <div className={styles.titleRow}>
                        <div className={styles.preview}>
                          <img
                            src={game.image ? `${PATH}${game.image}` : `${PATH}images/placeholders/game.jpg`}
                            alt={`Illustration du jeu ${game.nom}`}
                            className={styles.previewImage}
                          />
                        </div>
                        <h3><MarkdownRenderer content={game.nom} /></h3>
                      </div>
                          
                      <div className={styles.codeBlock}>
                        <span className={styles.code}>code: {game.code}</span>
                        <button type="button" className={styles.regenButton}
                          onClick={(e) => {
                            e.stopPropagation();
                            const confirmed = window.confirm("Êtes-vous sûr de vouloir générer un nouveau code ?");
                            if (!confirmed) return;
                            onRegenerateCode?.(game.id).then(newCode => {
                              setRegenMessages(prev => ({ ...prev, [game.id]: { type: 'success', message: 'Code mis à jour !' } }));
                            }).catch(() => {
                              setRegenMessages(prev => ({ ...prev, [game.id]: { type: 'error', message: 'Erreur serveur.' } }));
                            });
                          }}
                        >
                            Régénérer
                        </button>
                      </div>

                      {regenMessages[game.id] && (
                      <p className={ regenMessages[game.id].type === 'success' ? styles.success : styles.error }>
                        {regenMessages[game.id].message}
                      </p>
                      
                      )}
                    </div>

                    <hr className={styles.hr} />

                    <div className={styles.languageSelector}>
                      <div className={styles.langList}>
                        {relatedInfos.map((info) => (
                          <button
                            key={info.lang}
                            className={`${styles.langButton} ${selectedGameInfos?.lang === info.lang ? styles.active : ''}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleLangClick(game.id, info);
                            }}
                          >
                          <Flag lang={info.lang} /> {info.lang}
                          </button>
                        ))}

                        {Array.isArray(relatedInfos) && relatedInfos.length === 0 && ( <em>Aucune langue disponible</em> )}
                      </div>
                      
                      <button onClick={() => openAddModal(game.id)} className={styles.addLangButton} >
                          <Plus size={18}/> Ajouter une langue
                      </button>
                    </div>

                  </div>
                  
                  <div className={styles.actions}>
                    <button onClick={(e) => { e.stopPropagation(); openImageModal(game.id); }} title="Modifier l’image" >
                      <ImageIcon size={24} />
                    </button>
                    <button onClick={(e) => {
                      e.stopPropagation();
                      setEditingGame(game);
                      setEditedGameName(game.nom);
                      setEditedGameName_En(game.nom_en || "");
                      setEditedGameName_De(game.nom_de || "");
                      setEditedGameName_Es(game.nom_es || "");
                      setEditedGameName_It(game.nom_it || "");

                      // hydrate partenaire (si champs présents sur l'objet `game`)
                      setPartnerForm({
                          company_name:   game.company_name   || "",
                          company_address:game.company_address|| "",
                          company_phone:  game.company_phone  || "",
                          company_email:  game.company_email  || ""
                      });
                      setActiveEditTab('names');
                      setIsGameEditModalOpen(true);
                    }}>
                      <Pencil size={24} />
                    </button>
                    <button className={styles.delete} onClick={(e) => { e.stopPropagation(); onDeleteGame?.(game.id); }}>
                      <Trash2 size={24} />
                    </button>
                  </div>
              </div>

              <Collapse isOpen={isOpen}>
                {selectedGameInfos && (
                  <div id={`game-panel-${game.id}`}  className={styles.gameInfos}>
                    <div className={styles.infoItem}>
                      <div className={styles.langHeader}>
                        <span className={styles.lang}>{ALL_LANGUAGES.find(lang => lang.code === selectedGameInfos.lang)?.label || selectedGameInfos.lang}</span>

                        <button onClick={(e) => { e.stopPropagation(); openEditModal(selectedGameInfos); }} className={styles.modifyInfoBtn} >
                          <Pencil size={24} />
                        </button>

                        <div className={styles.active}>
                          <label>
                            <label className={styles.switch}>
                              <input 
                                type="checkbox" 
                                checked={isCheckedFor(selectedGameInfos)}
                                onChange={(e) => {
                                  onToggleGameInfosActive(selectedGameInfos.id, e.target.checked ? 1 : 0);
                                }}
                              />
                              <span className={styles.slider}></span>
                            </label>
                            &nbsp;Activer / Désactiver
                          </label>
                        </div>

                        <button className={styles.delete} onClick={(e) => { e.stopPropagation(); onDeleteGameInfos?.(selectedGameInfos.id); }}>
                          <Trash2 size={24} />
                        </button>
                      </div>

                      <hr className={styles.hr} />

                      <div>
                        <button
                          onClick={(e) => { e.stopPropagation(); onGameInfosSelect?.(selectedGameInfos); }}
                          className={styles.modifyQuestionsBtn}
                          title="Gérer les questions"
                          >
                          <Pencil size={18} />
                          Gérer les questions
                        </button>
                      </div>
                      <div>
                        <strong className={styles.labels}>INTRODUCTION :</strong>{' '}
                        {/* <MarkdownRenderer content={selectedGameInfos.intro} /> */}
                        <div className="rt-content">
                          <SmartRenderer content={selectedGameInfos.intro} />
                        </div>
                      </div>
                      <div>
                        <strong className={styles.labels}>CONCLUSION :</strong>{' '}
                        {/* <MarkdownRenderer content={selectedGameInfos.outro} /> */}
                        <div className="rt-content">
                          <SmartRenderer content={selectedGameInfos.outro} />
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </Collapse>
            </div>
          );
        })}

        {/* ======= Modale édition de l'image principale du jeu ======= */}
        {imageModalOpen && (
          <div className={styles.modalBackdrop}>
            <div className={styles.modalContent}>
              <h3>Mettre à jour l’image du jeu</h3>

              <ImageEditor
                initialImage={imagePreviewPath}
                canUpdatePreview={canUpdatePreview}
                onLocalChange={async (file) => {
                  setCanUpdatePreview(false); // désactive avant upload
                  try {
                    const response = await updateGameImage(imageTargetGameId, file);
                    setImageUpdateMessage({ text: response.message, success: response.success });
                    if (response.success) {
                      setCanUpdatePreview(true); // autorise la preview
                      setImagePreviewPath(`${PATH}${response.imagePath}`);
                      onGameImageUpdated?.(imageTargetGameId, response.imagePath);
                    } else {
                      setImageUpdateMessage({ text: "Erreur lors de l’upload.", success: false });
                    }
                    // setImageModalOpen(false); // ferme la modale après update
                  } catch (err) {
                    if (debug) console.error("Erreur upload image :", err);
                    setImageUpdateMessage({ text: "Erreur lors de l’upload.", success: false });
                  }
                }}
                onDelete={async () => {
                  // ici tu peux déclencher une suppression si tu l’implémentes
                }}
              />
              {imageUpdateMessage && (
              <p className={ imageUpdateMessage.success ? styles.success : styles.error } style={{ marginTop: '1em' }} >
                {imageUpdateMessage.text}
              </p>
              )}

              <div className={styles.modalActions}>
                <button 
                  className={styles.cancelButton}
                  onClick={() => {
                    setImageModalOpen(false)
                    setImageUpdateMessage('');
                  }} 
                >
                Fermer
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ======= Modale de gestion des langues du jeu (versions des parcours) ======= */}
        {isModalOpen && (
          <div className={styles.modalBackdrop}>
            <div className={styles.modalContent}>
              <h3>{modalMode === "edit" ? "Modifier la langue" : "Ajouter une langue"}</h3>

              <label>Langue</label><br />
              <select
                className={styles.languageDropdown}
                onChange={(e) => setFormLang(e.target.value)}
                value={formLang}
                >
                <option value="">-- Choisir une langue --</option>
                {/* Ajout de la langue en cours si absente dans la liste */}
                {modalMode === "edit" && !getAvailableLanguagesForGame(targetGameId).some(lang => lang.code === formLang) && (
                  <option value={formLang}>
                  {ALL_LANGUAGES.find(lang => lang.code === formLang)?.label || formLang}
                  </option>
                )}
                {getAvailableLanguagesForGame(targetGameId).map(({ code, label }) => (
                  <option key={code} value={code}>{label}</option>
                ))}
              </select>

              <label>Introduction</label>
              <RichTextEditor 
                value={formIntro} 
                onChange={({ markdown }) => setFormIntro(markdown)} 
                placeholder="Introduction du parcours…" 
              />

              <label>Conclusion</label>
              <RichTextEditor
                value={formOutro}
                onChange={({ markdown }) => setFormOutro(markdown)}
                placeholder="Conclusion du parcours…"
              />

              <div className={styles.modalActions}>
                <button onClick={closeModal} className={styles.cancelButton}>Annuler</button>
                <button onClick={handleModalSubmit}  className={styles.updateButton}>
                {modalMode === "edit" ? "Mettre à jour" : "Ajouter"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ======= Modale de modification des infos du jeu ======= */}
        {isGameEditModalOpen && (
          <div className={styles.modalBackdrop}>
            <div className={styles.modalContent}>
                
              {/* Onglets */}
              <div className={styles.tabs}>
                <button className={`${styles.tab} ${activeEditTab === 'names' ? styles.activeTab : ''}`} onClick={() => setActiveEditTab('names')} >
                Modifier le nom du jeu </button>
                <button className={`${styles.tab} ${activeEditTab === 'partner' ? styles.activeTab : ''}`} onClick={() => setActiveEditTab('partner')} >
                Infos partenaire </button>
              </div>

              {/* Contenu onglet "names" */}
              {activeEditTab === 'names' && (
                <>
                  <h3>Modifier le nom du jeu</h3>

                  <hr className={styles.hr} />

                  <input type="text" value={editedGameName} onChange={(e) => setEditedGameName(e.target.value)} className={styles.input} />

                  {existingNonFr.length > 0 && (
                    <>
                      <h3 className={styles.titleLeft}>Traduction du nom du jeu</h3>

                      <hr className={styles.hr} />

                      <div className={styles.langInlineRow}>
                      {getExistingLanguagesForGame(editingGame?.id, { exclude: ['fr'] }).map(({ code }) => (
                        <label key={code} className={styles.langInput} title={code.toUpperCase()}>
                          <span className={styles.flag}><Flag lang={code} /></span>
                          <input
                            type="text"
                            value={getLangValue(code)}
                            onChange={(e) => setLangValue(code, e.target.value)}
                            placeholder={code.toUpperCase()}
                            className={styles.inputLang}
                          />
                        </label>
                      ))}
                      </div>
                    </>
                  )}
                </>
              )}

              {/* Contenu onglet "partner" */}
              {activeEditTab === 'partner' && (
                <>
                  <h3>Infos partenaire</h3>

                  <hr className={styles.hr} />

                  <div className={styles.formGrid}>
                    <label>
                      <span>Nom de l’entreprise</span>
                      <input
                        name="company_name"
                        type="text"
                        value={partnerForm.company_name}
                        onChange={onPartnerChange}
                        placeholder="Atelier Hatann"
                      />
                    </label>

                    <label>
                      <span>Adresse</span>
                      <textarea
                        name="company_address"
                        rows={3}
                        value={partnerForm.company_address}
                        onChange={onPartnerChange}
                        placeholder="12 rue des Trésors, 35000 Rennes, France"
                      />
                    </label>

                    <label>
                      <span>Téléphone</span>
                      <input
                        name="company_phone"
                        type="tel"
                        value={partnerForm.company_phone}
                        onChange={onPartnerChange}
                        placeholder="+33 2 99 00 00 00"
                      />
                    </label>

                    <label>
                      <span>Email</span>
                      <input
                        name="company_email"
                        type="email"
                        value={partnerForm.company_email}
                        onChange={onPartnerChange}
                        placeholder="contact@exemple.com"
                      />
                    </label>
                  </div>
                </>
              )}

              <div className={styles.modalActions}>
                <button onClick={() => { setIsGameEditModalOpen(false); setEditingGame(null); }} className={styles.cancelButton} > Annuler </button>
                <button
                  onClick={async () => {
                    if (!editingGame) return;

                    const normalize = (s) => (s?.trim() ? s.trim() : null);

                    let payload = {};
                    if (activeEditTab === 'names') {
                      payload.nom = normalize(editedGameName); // FR
                      const existingNonFr = getExistingLanguagesForGame(editingGame.id, { exclude: ['fr'] }); // autres langues existantes ≠ FR
                      existingNonFr.forEach(({ code }) => {
                        payload[`nom_${code}`] = normalize(getLangValue(code));
                      });
                    } else {
                      payload = {
                        company_name:   normalize(partnerForm.company_name),
                        company_address:normalize(partnerForm.company_address),
                        company_phone:  normalize(partnerForm.company_phone),
                        company_email:  normalize(partnerForm.company_email),
                      };
                    }

                    const ok = await onUpdateGame?.(editingGame.id, payload); // onUpdateGame doit accepter (id, payload)
                    if (ok !== false) {
                    setIsGameEditModalOpen(false);
                    setEditingGame(null);
                    }
                  }}
                  className={styles.updateButton}
                >
                Mettre à jour
                </button>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );

}

export default GamesTab;
